/**
 * Stream hours, YouTube views and approximate sponsor ad time.
 *
 * Admins: every site's weekly hours, then one site's detail. Club managers:
 * their own site's detail only (the server enforces that, not just this page).
 */
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { reportsApi, queryKeys, type StreamReport, type StreamReportSite } from '@/lib/api';
import { useAuth } from '@/store/auth';

const RANGES = [4, 8, 12, 26];

function weekLabel(week: string): string {
  const d = new Date(`${week}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
}

function hours(h: number): string {
  return h === 0 ? '—' : h.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: h < 10 ? 1 : 0 });
}

function duration(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')} min`;
}

function count(n: number | null | undefined): string {
  return n ? n.toLocaleString('en-US') : '—';
}

function ViewsNote({ at }: { at: string | null }) {
  return (
    <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
      Views are YouTube&rsquo;s counts to date, replays included, so recent weeks keep growing.{' '}
      {at
        ? `Updated ${new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`
        : 'Not read from YouTube yet; the first update runs overnight.'}
    </p>
  );
}

const th = 'whitespace-nowrap px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const thLeft = 'whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const td = 'whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300';
const card = 'rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900';

function Overall({ report, onPick }: { report: StreamReport; onPick: (id: string) => void }) {
  const weeks = report.weeks;
  return (
    <>
      <section className={card}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">All sites</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{hours(report.totalHours)}</span> stream hours
            {' · '}<span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{count(report.totalViews)}</span> views over {weeks.length} weeks
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr><th className={thLeft}>Week of</th><th className={th}>Hours</th><th className={th}>Streams</th><th className={th}>Views</th></tr></thead>
            <tbody>
              {[...report.totals].reverse().map(t => (
                <tr key={t.week} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">{weekLabel(t.week)}</td>
                  <td className={`${td} font-semibold text-gray-900 dark:text-gray-100`}>{hours(t.hours)}</td>
                  <td className={td}>{t.streams || '—'}</td>
                  <td className={td}>{count(t.views)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ViewsNote at={report.viewsUpdatedAt} />
      </section>

      <section className={card}>
        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">By site</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Stream hours per week. Pick a site for its detail and ad time.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={thLeft}>Site</th>
                {weeks.map(w => <th key={w} className={th}>{weekLabel(w)}</th>)}
                <th className={th}>Total</th>
                <th className={th}>Views</th>
              </tr>
            </thead>
            <tbody>
              {report.sites.map(s => (
                <tr key={s.facilityId} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="whitespace-nowrap px-3 py-2">
                    <button type="button" onClick={() => onPick(s.facilityId)}
                      className="font-medium text-blue-700 hover:underline dark:text-blue-400">{s.name}</button>
                  </td>
                  {s.weeks.map(w => <td key={w.week} className={td}>{hours(w.hours)}</td>)}
                  <td className={`${td} font-semibold text-gray-900 dark:text-gray-100`}>{hours(s.totalHours)}</td>
                  <td className={td}>{count(s.totalViews)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function SiteDetail({ site, adhocSince, viewsAt }: { site: StreamReportSite; adhocSince: string | null; viewsAt: string | null }) {
  const totalAdSeconds = site.ads.reduce((a, x) => a + x.seconds, 0);
  const streamSeconds = site.totalHours * 3600;
  return (
    <>
      <section className={card}>
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{site.name}: stream hours</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{hours(site.totalHours)}</span> hours
            {' · '}<span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{count(site.totalViews)}</span> views over {site.weeks.length} weeks
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={thLeft}>Week of</th><th className={th}>Hours</th><th className={th}>League</th>
                <th className={th}>Events</th><th className={th}>Ad-hoc</th><th className={th}>Streams</th>
                <th className={th}>Views</th><th className={th} title="Most people watching one stream at once">Peak viewers</th>
              </tr>
            </thead>
            <tbody>
              {[...site.weeks].reverse().map(w => (
                <tr key={w.week} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">{weekLabel(w.week)}</td>
                  <td className={`${td} font-semibold text-gray-900 dark:text-gray-100`}>{hours(w.hours)}</td>
                  <td className={td}>{hours(w.scheduledHours)}</td>
                  <td className={td}>{hours(w.eventHours)}</td>
                  <td className={td}>{hours(w.adhocHours)}</td>
                  <td className={td}>{w.streams || '—'}</td>
                  <td className={td}>{count(w.views)}</td>
                  <td className={td}>{count(w.peakViewers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!adhocSince || site.weeks.some(w => w.week < adhocSince.slice(0, 10)) ? (
          <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
            Ad-hoc streams are counted from {adhocSince ? new Date(adhocSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'the next ad-hoc stream'} on; earlier ones were not recorded.
          </p>
        ) : null}
        <ViewsNote at={viewsAt} />
      </section>

      <section className={card}>
        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{site.name}: most watched</h2>
        </div>
        {site.topVideos.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">No YouTube view counts in this period yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr><th className={thLeft}>Stream</th><th className={thLeft}>Date</th><th className={thLeft}>Sheet</th><th className={th}>Views</th><th className={th}>Peak viewers</th></tr>
              </thead>
              <tbody>
                {site.topVideos.map(v => (
                  <tr key={v.videoId} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="max-w-[22rem] truncate px-3 py-2" title={v.title}>
                      <a href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.videoId)}`} target="_blank" rel="noopener noreferrer"
                        className="text-blue-700 hover:underline dark:text-blue-400">{v.title || v.videoId}</a>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-300">
                      {new Date(v.start).toLocaleDateString('en-US', { timeZone: site.timezone, month: 'short', day: 'numeric' })}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-300">{v.sheetId ?? '—'}</td>
                    <td className={`${td} font-semibold text-gray-900 dark:text-gray-100`}>{count(v.views)}</td>
                    <td className={td}>{count(v.peakViewers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={card}>
        <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{site.name}: sponsor ad time (approximate)</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Each image&rsquo;s share of stream time on the sheets that show it, from its set duration in the slideshow. Based on each overlay&rsquo;s current slideshow.
          </p>
        </div>
        {site.ads.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">No sponsor slideshow time in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={thLeft}>Image</th><th className={th}>Time on air</th><th className={th}>Times shown</th><th className={th}>Share of stream time</th>
                  <th className={th} title="YouTube views of the games this image ran in">Views of its games</th>
                </tr>
              </thead>
              <tbody>
                {site.ads.map(a => (
                  <tr key={a.key} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="max-w-[18rem] truncate px-3 py-2 text-gray-900 dark:text-gray-100" title={a.name}>{a.name}</td>
                    <td className={td}>{duration(a.seconds)}</td>
                    <td className={td}>{a.plays.toLocaleString('en-US')}</td>
                    <td className={td}>{streamSeconds > 0 ? `${Math.round((a.seconds / streamSeconds) * 100)}%` : '—'}</td>
                    <td className={td}>{count(a.views)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td className="px-3 py-2 font-semibold text-gray-900 dark:text-gray-100">All images</td>
                  <td className={`${td} font-semibold text-gray-900 dark:text-gray-100`}>{duration(totalAdSeconds)}</td>
                  <td className={td} /><td className={td} /><td className={td} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [weeks, setWeeks] = useState(8);
  const [siteId, setSiteId] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.reports.streams(weeks),
    queryFn: () => reportsApi.getStreamReport(weeks),
    staleTime: 5 * 60 * 1000,
  });

  const sites = useMemo(() => data?.sites ?? [], [data]);
  useEffect(() => {
    if (!isAdmin && sites[0] && siteId !== sites[0].facilityId) setSiteId(sites[0].facilityId);
  }, [isAdmin, sites, siteId]);
  const site = useMemo(() => sites.find(s => s.facilityId === siteId) ?? null, [sites, siteId]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-100">
            <BarChart3 className="h-5 w-5" /> Stream Report
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Weeks run Monday to Sunday in each site&rsquo;s local time.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <select value={siteId} onChange={e => setSiteId(e.target.value)} aria-label="Site"
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
              <option value="">All sites</option>
              {sites.map(s => <option key={s.facilityId} value={s.facilityId}>{s.name}</option>)}
            </select>
          )}
          <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} aria-label="Period"
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
            {RANGES.map(n => <option key={n} value={n}>Last {n} weeks</option>)}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading report…</div>
      ) : error ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          Could not load the report. {(error as Error).message}
        </p>
      ) : !data || sites.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No sites to report on.</p>
      ) : (
        <>
          {isAdmin && site && (
            <button type="button" onClick={() => setSiteId('')} className="text-sm text-blue-700 hover:underline dark:text-blue-400">← All sites</button>
          )}
          {isAdmin && !site ? <Overall report={data} onPick={setSiteId} /> : site ? <SiteDetail site={site} adhocSince={data.adhocRecordedSince} viewsAt={data.viewsUpdatedAt} /> : null}
        </>
      )}
    </div>
  );
}
